import { ArrowSortDownRegular, ArrowSortRegular, ArrowSortUpRegular } from '@fluentui/react-icons';
import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { type KeyboardEvent, type PointerEvent, useCallback, useMemo, useState } from 'react';

import { HubApiError } from '@/api/client';
import type { ElevationRequest, ElevationStatus } from '@/api/types';
import { ErrorState, LoadingState, SectionCard, StatusPill, type StatusKind } from '@/design-system';
import { useHubToast } from '@/design-system/toast';
import { HUB_ENVS, HUB_ENV_FULL_LABEL, type HubEnv } from '@/env/types';
import { spacing } from '@/theme/tokens';

import {
  useElevationPresets,
  useElevationRequests,
  useRevokeElevation,
  type ElevationListFilter,
} from '../api';
import { presetDisplayName } from '../presetDisplayName';
import {
  DEFAULT_ELEVATION_HISTORY_SORT,
  sortElevationHistory,
  type ElevationHistorySortColumn,
} from './elevationHistorySort';

const STATUS_VALUES: ElevationStatus[] = [
  'Pending',
  'AutoApproved',
  'Approved',
  'Rejected',
  'Expired',
  'Revoked',
];

const STATUS_TONE: Record<ElevationStatus, StatusKind> = {
  Pending: 'unknown',
  AutoApproved: 'healthy',
  Approved: 'healthy',
  Rejected: 'unhealthy',
  Expired: 'unknown',
  Revoked: 'degraded',
};

const useStyles = makeStyles({
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.s,
    marginBottom: spacing.l,
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
  },
  header: {
    position: 'relative',
    paddingRight: tokens.spacingHorizontalL,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  sortButton: {
    color: 'inherit',
    font: 'inherit',
    fontWeight: 'inherit',
    backgroundColor: 'transparent',
    border: 0,
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  resizeHandle: {
    position: 'absolute',
    insetBlock: tokens.spacingVerticalXS,
    insetInlineEnd: 0,
    width: tokens.spacingHorizontalM,
    cursor: 'col-resize',
    outline: 'none',
    ':focus-visible': {
      backgroundColor: tokens.colorBrandBackground2,
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  scope: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
    width: '100%',
    display: 'inline-block',
  },
  tnum: {
    fontFeatureSettings: '"tnum" 1',
  },
  actions: {
    display: 'flex',
    gap: spacing.xs,
  },
});

// Landing-page default. The /elevation/history route renders this same
// component with a wider `windowMs` prop (see HistoryPage.tsx), while the
// table still computes its own from/to timestamps.
const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

const MINIMUM_COLUMN_WIDTH = 96;
const MAXIMUM_COLUMN_WIDTH = 640;
const KEYBOARD_RESIZE_STEP = 16;
const ELEVATION_HISTORY_WIDTHS_KEY = 'hub:elevation-history-widths:v1';

interface ElevationHistoryColumn {
  id: string;
  label: string;
  width: number;
  minWidth?: number;
  maxWidth?: number;
  sortColumn?: ElevationHistorySortColumn;
  resizable?: boolean;
}

const ELEVATION_HISTORY_COLUMNS: readonly ElevationHistoryColumn[] = [
  { id: 'requestedAt', label: 'Requested at', width: 180, sortColumn: 'requestedAt' },
  { id: 'env', label: 'Env', width: 96 },
  { id: 'status', label: 'Status', width: 132, sortColumn: 'status' },
  { id: 'preset', label: 'Preset', width: 200, sortColumn: 'preset' },
  { id: 'requester', label: 'Requester', width: 220, sortColumn: 'requester' },
  { id: 'scope', label: 'Scope', width: 320, minWidth: 180 },
  { id: 'decidedAt', label: 'Decided at', width: 180, sortColumn: 'decidedAt' },
  { id: 'expiresAt', label: 'Expires at', width: 180, sortColumn: 'expiresAt' },
  { id: 'decidedBy', label: 'Decided by', width: 220 },
  { id: 'actions', label: 'Actions', width: 96, resizable: false },
];

function loadColumnWidths(): Partial<Record<string, number>> {
  try {
    const stored = window.localStorage.getItem(ELEVATION_HISTORY_WIDTHS_KEY);
    if (stored === null) return {};
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
    );
  } catch {
    return {};
  }
}

function fmtTime(ms: number | null | undefined) {
  return ms ? new Date(ms).toLocaleString() : '—';
}

function fmtRemaining(expiresAt: number | null | undefined) {
  if (!expiresAt) return '—';
  const left = expiresAt - Date.now();
  if (left <= 0) return 'expired';
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  return h > 0 ? `${String(h)}h ${String(m)}m` : `${String(m)}m`;
}

interface ProblemDetails {
  title?: string;
  detail?: string;
}

const isProblemDetails = (value: unknown): value is ProblemDetails =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function formatHubApiError(err: Error): string {
  if (err instanceof HubApiError && isProblemDetails(err.body)) {
    if (typeof err.body.detail === 'string' && err.body.detail.trim().length > 0) {
      return err.body.detail;
    }
    if (typeof err.body.title === 'string' && err.body.title.trim().length > 0) {
      return err.body.title;
    }
  }
  if (err instanceof HubApiError && typeof err.body === 'string' && err.body.trim().length > 0) {
    return err.body;
  }
  return err.message;
}

interface HistoryHeaderCellProps {
  column: ElevationHistoryColumn;
  width: number;
  sort: typeof DEFAULT_ELEVATION_HISTORY_SORT;
  onResize: (column: ElevationHistoryColumn, width: number) => void;
  onSort: (column: ElevationHistorySortColumn) => void;
}

function HistoryHeaderCell({ column, width, sort, onResize, onSort }: HistoryHeaderCellProps) {
  const classes = useStyles();
  const direction =
    column.sortColumn === undefined ? undefined : sort.column === column.sortColumn ? sort.direction : 'none';
  const SortIcon =
    direction === 'ascending'
      ? ArrowSortUpRegular
      : direction === 'descending'
        ? ArrowSortDownRegular
        : ArrowSortRegular;
  const minWidth = column.minWidth ?? MINIMUM_COLUMN_WIDTH;
  const maxWidth = column.maxWidth ?? MAXIMUM_COLUMN_WIDTH;

  const startResize = (event: PointerEvent<HTMLInputElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const onPointerMove = (moveEvent: globalThis.PointerEvent) => {
      onResize(column, width + moveEvent.clientX - startX);
    };
    const stopResize = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', stopResize);
      document.removeEventListener('pointercancel', stopResize);
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopResize, { once: true });
    document.addEventListener('pointercancel', stopResize, { once: true });
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onResize(column, width - KEYBOARD_RESIZE_STEP);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onResize(column, width + KEYBOARD_RESIZE_STEP);
    } else if (event.key === 'Home') {
      event.preventDefault();
      onResize(column, minWidth);
    } else if (event.key === 'End') {
      event.preventDefault();
      onResize(column, maxWidth);
    }
  };

  return (
    <TableHeaderCell className={classes.header} aria-label={column.label} aria-sort={direction}>
      <div className={classes.headerContent}>
        {column.sortColumn === undefined ? (
          <span>{column.label}</span>
        ) : (
          <button
            type="button"
            className={classes.sortButton}
            aria-label={`Sort by ${column.label}`}
            onClick={() => {
              if (column.sortColumn !== undefined) onSort(column.sortColumn);
            }}
          >
            {column.label} <SortIcon aria-hidden="true" />
          </button>
        )}
      </div>
      {column.resizable !== false ? (
        <input
          type="range"
          aria-label={`Resize ${column.label} column`}
          aria-orientation="horizontal"
          min={minWidth}
          max={maxWidth}
          value={width}
          className={classes.resizeHandle}
          onChange={(event) => {
            onResize(column, Number(event.target.value));
          }}
          onPointerDown={startResize}
          onKeyDown={handleResizeKeyDown}
        />
      ) : null}
    </TableHeaderCell>
  );
}

export interface ElevationHistoryProps {
  /**
   * Width of the lookback window in milliseconds. Defaults to 30 days for the
   * `/elevation` landing view; `/elevation/history` passes `730 * 86_400_000`
   * (2 years — matches the Cosmos `elevationRequests.defaultTtl` ceiling).
   */
  windowMs?: number;
  /** Section title; defaults to "Recent activity (last 30 days)". */
  title?: string;
  /** Section description; defaults to the 30-day landing copy. */
  description?: string;
  /**
   * Maximum rows pulled back. The API clamps to 1000 rows; this component
   * defaults to 500 so the landing view stays lighter-weight.
   */
  limit?: number;
}

export function ElevationHistory({
  windowMs = thirtyDaysMs,
  title = 'Recent activity (last 30 days)',
  description = 'Elevation requests you can access from the last 30 days. Open the 2-year view (up to 1000 rows) for a wider window.',
  limit = 500,
}: ElevationHistoryProps = {}) {
  const classes = useStyles();
  const toast = useHubToast();
  const revoke = useRevokeElevation();
  const presets = useElevationPresets();

  const [envFilter, setEnvFilter] = useState<HubEnv | ''>('');
  const [statusFilter, setStatusFilter] = useState<ElevationStatus | ''>('');
  const [requesterFilter, setRequesterFilter] = useState('');
  const [sort, setSort] = useState(DEFAULT_ELEVATION_HISTORY_SORT);
  const [columnWidths, setColumnWidths] = useState(loadColumnWidths);

  // Lookback window, bucketed to the day so memoization isn't invalidated
  // every render. Captured once on mount to satisfy the react-hooks purity
  // rule.
  const [dayBucket] = useState(() => {
    const day = 86_400_000;
    return Math.floor(Date.now() / day) * day;
  });

  const filter: ElevationListFilter = useMemo(
    () => ({
      env: envFilter || undefined,
      status: statusFilter || undefined,
      requester: requesterFilter.trim() || undefined,
      fromEpochMs: dayBucket - windowMs,
      toEpochMs: dayBucket + 86_400_000,
      limit,
    }),
    [envFilter, statusFilter, requesterFilter, dayBucket, windowMs, limit],
  );

  const query = useElevationRequests(filter);
  const sortedRequests = useMemo(
    () => sortElevationHistory(query.data ?? [], sort, presets.data),
    [query.data, sort, presets.data],
  );

  const handleSort = (column: ElevationHistorySortColumn) => {
    setSort((current) => ({
      column,
      direction: current.column === column && current.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const updateColumnWidth = useCallback((column: ElevationHistoryColumn, requestedWidth: number) => {
    const minWidth = column.minWidth ?? MINIMUM_COLUMN_WIDTH;
    const maxWidth = column.maxWidth ?? MAXIMUM_COLUMN_WIDTH;
    const width = Math.min(maxWidth, Math.max(minWidth, requestedWidth));
    setColumnWidths((current) => {
      const next = { ...current, [column.id]: width };
      try {
        window.localStorage.setItem(ELEVATION_HISTORY_WIDTHS_KEY, JSON.stringify(next));
      } catch {
        // The selected width remains available while this page is open.
      }
      return next;
    });
  }, []);

  const handleRevoke = (req: ElevationRequest) => {
    const reason = window.prompt('Revoke reason:');
    if (!reason) return;
    revoke.mutate(
      { id: req.id, reason },
      {
        onSuccess: () => {
          toast({ intent: 'info', title: 'Revoked', body: req.id.slice(0, 8) });
        },
        onError: (err) => {
          toast({ intent: 'error', title: 'Revoke failed', body: err.message });
        },
      },
    );
  };

  return (
    <SectionCard title={title} description={description}>
      <div className={classes.filters}>
        <Field label="Environment">
          <Dropdown
            value={envFilter ? HUB_ENV_FULL_LABEL[envFilter] : 'All'}
            selectedOptions={envFilter ? [envFilter] : []}
            onOptionSelect={(_, data) => {
              setEnvFilter((data.optionValue as HubEnv | '' | undefined) ?? '');
            }}
          >
            <Option value="">All</Option>
            {HUB_ENVS.map((e) => (
              <Option key={e} value={e}>
                {HUB_ENV_FULL_LABEL[e]}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Status">
          <Dropdown
            value={statusFilter || 'All'}
            selectedOptions={statusFilter ? [statusFilter] : []}
            onOptionSelect={(_, data) => {
              setStatusFilter((data.optionValue as ElevationStatus | '' | undefined) ?? '');
            }}
          >
            <Option value="">All</Option>
            {STATUS_VALUES.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Requester (UPN)">
          <Input
            placeholder="anyone@caredocs.ai"
            value={requesterFilter}
            onChange={(_, data) => {
              setRequesterFilter(data.value);
            }}
          />
        </Field>
      </div>

      {query.isLoading ? (
        <LoadingState label="Loading history…" />
      ) : query.error ? (
        <ErrorState
          title="Could not load history"
          description={query.error instanceof Error ? formatHubApiError(query.error) : undefined}
          retry={() => void query.refetch()}
        />
      ) : (query.data?.length ?? 0) === 0 ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>No requests match the current filter.</Text>
      ) : (
        <div className={classes.tableContainer}>
          <Table aria-label="Elevation history" size="small" className={classes.table}>
            <colgroup>
              {ELEVATION_HISTORY_COLUMNS.map((column) => (
                <col key={column.id} style={{ width: columnWidths[column.id] ?? column.width }} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow>
                {ELEVATION_HISTORY_COLUMNS.map((column) => (
                  <HistoryHeaderCell
                    key={column.id}
                    column={column}
                    width={columnWidths[column.id] ?? column.width}
                    sort={sort}
                    onResize={updateColumnWidth}
                    onSort={handleSort}
                  />
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRequests.map((row) => {
                const isActive = row.status === 'Approved' || row.status === 'AutoApproved';
                return (
                  <TableRow key={row.id}>
                    <TableCell className={classes.tnum}>{fmtTime(row.createdAt)}</TableCell>
                    <TableCell>{row.env}</TableCell>
                    <TableCell>
                      <StatusPill kind={STATUS_TONE[row.status]} label={row.status} />
                    </TableCell>
                    <TableCell>
                      {presetDisplayName(row.presetKey, presets.data)}
                      {presetDisplayName(row.presetKey, presets.data) !== row.presetKey && (
                        <Text as="span" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          {' '}
                          ({row.presetKey})
                        </Text>
                      )}
                    </TableCell>
                    <TableCell>{row.requesterUpn}</TableCell>
                    <TableCell>
                      <span className={classes.scope}>{row.scope}</span>
                    </TableCell>
                    <TableCell className={classes.tnum}>{fmtTime(row.decidedAt)}</TableCell>
                    <TableCell className={classes.tnum}>
                      {isActive ? fmtRemaining(row.expiresAt) : fmtTime(row.expiresAt)}
                    </TableCell>
                    <TableCell>{row.approverUpn ?? '—'}</TableCell>
                    <TableCell>
                      {isActive ? (
                        <div className={classes.actions}>
                          <Button
                            size="small"
                            appearance="subtle"
                            onClick={() => {
                              handleRevoke(row);
                            }}
                          >
                            Revoke
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </SectionCard>
  );
}
